import React, { useRef, useState, useMemo, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Register pixel-based font sizes via style attributor
const SizeStyle = Quill.import('attributors/style/size') as any;
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px'];
try {
  Quill.register(SizeStyle, true);
} catch (_) {}

// Register HR divider blot — guard against double-registration
const BlockEmbed = Quill.import('blots/block/embed') as any;
class DividerBlot extends BlockEmbed {
  static blotName = 'divider';
  static tagName = 'hr';
}
try {
  Quill.register(DividerBlot, true);
} catch (_) {}

const SPECIAL_CHARS = [
  '©', '®', '™', '°', '±', '×', '÷', '≠', '≤', '≥', '∞', '∑', '√', '∂',
  '½', '⅓', '¼', '¾', '⅔', '⅛', '⅜', '⅝', '⅞',
  '←', '→', '↑', '↓', '↔', '⇒', '⇐', '⇔',
  '§', '¶', '†', '‡', '•', '·', '…', '—', '–', '\u201C', '\u201D', '\u2018', '\u2019',
  '€', '£', '¥', '¢', '¤',
  'α', 'β', 'γ', 'δ', 'μ', 'π', 'σ', 'φ', 'ω',
];

const FORMATS = [
  'size', 'bold', 'italic', 'underline',
  'list', 'bullet',
  'divider'
];

interface WYSIWYGEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const WYSIWYGEditor: React.FC<WYSIWYGEditorProps> = ({ value, onChange, placeholder }) => {
  const quillRef = useRef<ReactQuill>(null);
  const [specialCharsOpen, setSpecialCharsOpen] = useState(false);
  const [savedRange, setSavedRange] = useState<any>(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ size: ['10px', '12px', '14px', false, '18px', '20px', '24px', '28px', '32px', '36px'] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['divider', 'special'],
      ],
      handlers: {
        divider: () => {
          const quill = quillRef.current?.getEditor();
          if (!quill) return;
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'divider', true, 'user');
          quill.setSelection(range.index + 1, 0);
        },
        special: () => {
          const quill = quillRef.current?.getEditor();
          if (quill) setSavedRange(quill.getSelection());
          setSpecialCharsOpen(true);
        }
      }
    }
  }), []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const toolbar = (quill as any).container.previousElementSibling as HTMLElement;
    if (!toolbar) return;

    // Set tooltips
    const titles: Array<[string, string]> = [
      ['button.ql-bold', 'Bold'],
      ['button.ql-italic', 'Italic'],
      ['button.ql-underline', 'Underline'],
      ['button.ql-list[value="ordered"]', 'Numbered List'],
      ['button.ql-list[value="bullet"]', 'Bullet List'],
      ['.ql-size .ql-picker-label', 'Font Size'],
      ['button.ql-divider', 'Insert Divider'],
      ['button.ql-special', 'Special Characters'],
    ];
    titles.forEach(([selector, title]) => {
      const el = toolbar.querySelector(selector) as HTMLElement;
      if (el) el.title = title;
    });

    // Inject SVG icons into custom buttons
    const dividerBtn = toolbar.querySelector('button.ql-divider') as HTMLElement;
    if (dividerBtn && !dividerBtn.querySelector('svg')) {
      dividerBtn.innerHTML = `<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <rect class="ql-fill" x="1" y="8.5" width="16" height="1.5" rx="0.5"/>
      </svg>`;
    }

    const specialBtn = toolbar.querySelector('button.ql-special') as HTMLElement;
    if (specialBtn && !specialBtn.querySelector('svg')) {
      specialBtn.innerHTML = `<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <text class="ql-fill" x="2" y="14" font-size="13" font-family="serif" font-weight="bold">Ω</text>
      </svg>`;
    }
  }, []);

  const insertSpecialChar = (char: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = savedRange || quill.getSelection(true);
    if (range) {
      quill.insertText(range.index, char, 'user');
      quill.setSelection(range.index + char.length, 0);
    }
    setSpecialCharsOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #DDDDDD',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        '& .ql-toolbar.ql-snow': {
          border: 'none',
          borderBottom: '1px solid #DDDDDD',
          backgroundColor: '#f8f8f8',
          borderRadius: '8px 8px 0 0',
          padding: '6px 8px',
          lineHeight: 'normal',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '2px'
        },
        '& .ql-toolbar.ql-snow .ql-formats': {
          marginRight: '4px'
        },
        '& .ql-toolbar.ql-snow button': {
          width: '28px',
          height: '24px',
          padding: '2px 4px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        },
        '& .ql-toolbar.ql-snow button svg': {
          width: '18px',
          height: '18px',
          float: 'none',
          display: 'block'
        },
        '& .ql-toolbar.ql-snow button .ql-stroke': { stroke: '#444444' },
        '& .ql-toolbar.ql-snow button .ql-fill': { fill: '#444444' },
        '& .ql-toolbar.ql-snow button:hover .ql-stroke': { stroke: '#1976D2' },
        '& .ql-toolbar.ql-snow button:hover .ql-fill': { fill: '#1976D2' },
        '& .ql-toolbar.ql-snow button.ql-active .ql-stroke': { stroke: '#1976D2' },
        '& .ql-toolbar.ql-snow button.ql-active .ql-fill': { fill: '#1976D2' },
        // Font size picker
        '& .ql-snow .ql-picker.ql-size': { width: '80px' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-options': {
          maxHeight: '180px',
          overflowY: 'auto'
        },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label::before': { content: '"Size"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value]::before': { content: 'attr(data-value)' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item::before': { content: '"Normal"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value]::before': { content: 'attr(data-value)' },
        '& .ql-container.ql-snow': {
          border: 'none',
          borderRadius: '0 0 8px 8px',
          minHeight: '300px',
          fontSize: '15px'
        },
        '& .ql-editor': { minHeight: '300px' }
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />

      {/* Special Characters Dialog */}
      <Dialog open={specialCharsOpen} onClose={() => setSpecialCharsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Special Characters</Typography>
          <IconButton size="small" onClick={() => setSpecialCharsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {SPECIAL_CHARS.map((char) => (
              <Box
                key={char}
                onClick={() => insertSpecialChar(char)}
                sx={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#E3F2FD', borderColor: '#1976D2' }
                }}
              >
                {char}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WYSIWYGEditor;
